import React, { useState } from "react";
import {
  Card,
  CardContent,
  IconButton,
  Chip,
  Tooltip,
  Box,
} from "@mui/material";
import {
  Assessment,
  Lock,
  CheckCircle,
  ArrowForward,
  CalendarToday,
} from "@mui/icons-material";

const ReportItem = ({ direct, selectedStep, available, unlocked }) => {
  const [isHovered, setIsHovered] = useState(false);
  const isAvailable = available && unlocked;
  const reportName = direct.customName || direct.name;

  return (
    <Card
      className={`h-full w-full transition-all duration-500 ease-out cursor-pointer overflow-hidden
        ${
          isAvailable
            ? "hover:shadow-xl bg-gradient-to-br from-white to-emerald-50 border border-emerald-200 hover:border-emerald-300"
            : "bg-gray-50 border border-gray-200 hover:border-gray-300"
        }
        ${isHovered && isAvailable ? "transform scale-105" : ""}
      `}
      elevation={0}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => (isAvailable ? selectedStep(direct) : null)}
    >
      <CardContent className="p-6 h-full flex flex-col relative">
        <div
          className={`absolute top-0 left-0 w-full h-1 transition-all duration-500 ease-out 
          ${
            isAvailable
              ? "bg-gradient-to-r from-emerald-500 to-emerald-600"
              : "bg-gray-300"
          }
          ${isHovered && isAvailable ? "h-2" : ""}
        `}
        />
        <div className="flex items-start justify-between mb-4 mt-2">
          <div
            className={`p-3 rounded-xl transition-all duration-500 ease-out ${
              isAvailable
                ? "bg-emerald-100 text-emerald-700"
                : "bg-gray-200 text-gray-500"
            } ${
              isHovered && isAvailable
                ? "bg-emerald-200 transform scale-110"
                : ""
            }`}
          >
            <Assessment className="h-6 w-6" />
          </div>

          <Box>
            {isAvailable ? (
              <Chip
                icon={<CheckCircle fontSize="small" />}
                label="Disponível"
                size="small"
                className="bg-emerald-100 text-emerald-800 border border-emerald-200 font-medium"
              />
            ) : (
              <Chip
                icon={<Lock fontSize="small" />}
                label="Bloqueado"
                size="small"
                className="bg-gray-200 text-gray-600 border border-gray-300"
              />
            )}
          </Box>
        </div>

        <div className="flex-1 mb-4">
          <h3
            className={`text-lg font-semibold mb-3 leading-tight transition-colors duration-500 ease-out ${
              isAvailable ? "text-gray-800" : "text-gray-500"
            } ${isHovered && isAvailable ? "text-emerald-800" : ""}`}
          >
            {reportName}
          </h3>
          <p
            className={`text-sm leading-relaxed transition-colors duration-500 ease-out ${
              isAvailable ? "text-gray-600" : "text-gray-400"
            }`}
          >
            {getReportDescription(reportName)}
          </p>
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <div className="flex items-center gap-2">
            <div
              className={`w-2 h-2 rounded-full transition-all duration-500 ease-out ${
                isAvailable ? "bg-emerald-500" : "bg-gray-400"
              } ${
                isHovered && isAvailable
                  ? "bg-emerald-600 transform scale-125"
                  : ""
              }`}
            />
            <span
              className={`text-xs font-medium transition-colors duration-500 ease-out ${
                isAvailable ? "text-emerald-700" : "text-gray-500"
              }`}
            >
              {isAvailable ? "Pronto para visualizar" : "Aguardando conclusão"}
            </span>
          </div>

          {isAvailable ? (
            <Tooltip title="Visualizar relatório" arrow placement="top">
              <IconButton
                size="small"
                className={`bg-emerald-600 text-white hover:bg-emerald-700 transition-all duration-500 ease-out ${
                  isHovered ? "transform scale-110 bg-emerald-700" : ""
                }`}
              >
                <ArrowForward className="h-4 w-4" />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip
              title="Complete a etapa para desbloquear"
              arrow
              placement="top"
            >
              <span className="text-xs text-gray-400 flex items-center">
                <CalendarToday fontSize="small" className="mr-1 h-3 w-3" />
                Bloqueado
              </span>
            </Tooltip>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const getReportDescription = (reportName) => {
  const descriptions = {
    "Dados dos beneficiarios":
      "Visualize informações demográficas e estatísticas dos beneficiários do sistema",
    "Dimensionamento por Complexidade":
      "Análise da capacidade e dimensionamento da rede de leitos hospitalares",
    "Estimativas Epidemiológicas":
      "Projeções e estimativas baseadas em dados epidemiológicos da região",
    "Dimensionamento por Especialidades":
      "Análise detalhada das necessidades por categoria de serviço médico",
    "Gestão de Rede":
      "Status de acompanhamento das atividades",
    "Relatório de Utilização da Operadora":
      "Análise dinâmica de médias e indicadores de utilização",
    "Distribuição da Rede Prestadora":
      "Análise da distribuição geográfica e funcional dos colaboradores na rede de saúde",
  };

  return (
    descriptions[reportName] ||
    "Relatório detalhado com análises e informações relevantes do sistema"
  );
};

export default ReportItem;
